<script setup>
// Assistente por chat: sem blocos ainda (ou pedido explícito) = compor do
// briefing (cobra créditos); com conteúdo = editar por instrução (grátis).
// Também sugere assuntos. O histórico vive só nesta tela. Sem editar
// (`canEdit` falso, editor v2) compor troca o e-mail inteiro: com conteúdo,
// avisa e pede confirmação antes.
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import API from 'dashboard/api/marketingEmailTemplates';

const props = defineProps({
  template: { type: Object, required: true },
  source: { type: Function, required: true },
  hasContent: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: true },
});
const emit = defineEmits(['apply', 'subject', 'error']);
const { t } = useI18n();

const messages = ref([]);
const input = ref('');
const busy = ref(false);
const listRef = ref(null);
const subjects = ref([]);
const pending = ref('');
const replaces = computed(() => props.hasContent && !props.canEdit);

const chips = computed(() => {
  if (props.hasContent && props.canEdit)
    return ['SHORTER', 'FORMAL', 'CASUAL', 'SUBJECTS'];
  return props.hasContent
    ? ['PROPOSAL', 'WELCOME', 'REACTIVATE', 'SUBJECTS']
    : ['PROPOSAL', 'WELCOME', 'REACTIVATE'];
});

const push = (role, text) => {
  messages.value.push({ role, text });
  nextTick(() => {
    if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight;
  });
};

const run = async (text, { confirmed = false } = {}) => {
  const clean = text.trim();
  if (!clean || busy.value) return;
  if (replaces.value && !confirmed) {
    pending.value = clean;
    input.value = '';
    return;
  }
  pending.value = '';
  push('user', clean);
  input.value = '';
  busy.value = true;
  try {
    const { data } =
      props.hasContent && props.canEdit
        ? await API.aiEdit(props.template.id, clean, props.source())
        : await API.aiCompose({
            id: props.template.id,
            brief: clean,
            mode: props.template.mode,
          });
    emit('apply', data);
    push('ai', data.summary || t('EMAIL_TEMPLATES.BUILDER.AI.DONE'));
  } catch (error) {
    const msg =
      error?.response?.data?.error || t('EMAIL_TEMPLATES.BUILDER.AI.FAILED');
    push('ai', msg);
    emit('error', msg);
  } finally {
    busy.value = false;
  }
};

const suggestSubjects = async () => {
  busy.value = true;
  push('user', t('EMAIL_TEMPLATES.BUILDER.AI.CHIPS.SUBJECTS'));
  try {
    const { data } = await API.aiSubjects(props.template.id, props.source());
    subjects.value = data.subjects || [];
    push('ai', t('EMAIL_TEMPLATES.BUILDER.AI.SUBJECTS_READY'));
  } catch (error) {
    push(
      'ai',
      error?.response?.data?.error || t('EMAIL_TEMPLATES.BUILDER.AI.FAILED')
    );
  } finally {
    busy.value = false;
  }
};

const onChip = chip => {
  if (chip === 'SUBJECTS') return suggestSubjects();
  return run(t(`EMAIL_TEMPLATES.BUILDER.AI.PROMPTS.${chip}`));
};
</script>

<template>
  <div class="flex h-full min-h-0 flex-col">
    <div
      ref="listRef"
      class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3"
    >
      <p
        v-if="replaces"
        data-ai-replace-note
        class="rounded-lg border border-n-amber-6 bg-n-amber-2 p-3 text-xs leading-relaxed text-n-amber-11"
      >
        {{ t('EMAIL_TEMPLATES.BUILDER.AI.REPLACE_NOTE') }}
      </p>
      <p
        v-else-if="!messages.length"
        class="rounded-lg bg-n-solid-2 p-3 text-xs leading-relaxed text-n-slate-11"
      >
        {{
          hasContent
            ? t('EMAIL_TEMPLATES.BUILDER.AI.HINT_EDIT')
            : t('EMAIL_TEMPLATES.BUILDER.AI.HINT_COMPOSE')
        }}
      </p>
      <div
        v-for="(m, i) in messages"
        :key="i"
        class="max-w-[92%] rounded-xl px-3 py-2 text-xs leading-relaxed"
        :class="
          m.role === 'user'
            ? 'self-end rounded-br-sm bg-n-brand text-white'
            : 'self-start rounded-bl-sm bg-n-solid-2 text-n-slate-12'
        "
      >
        {{ m.text }}
      </div>
      <div v-if="subjects.length" class="flex flex-col gap-1">
        <button
          v-for="s in subjects"
          :key="s"
          type="button"
          class="rounded-lg border border-n-weak px-2.5 py-1.5 text-left text-xs text-n-slate-12 hover:border-n-brand"
          @click="emit('subject', s)"
        >
          {{ s }}
        </button>
      </div>
      <div v-if="busy" class="self-start text-xs text-n-slate-10">
        <span class="i-lucide-loader-2 animate-spin" />
        {{ t('EMAIL_TEMPLATES.BUILDER.AI.THINKING') }}
      </div>
    </div>
    <div
      v-if="pending"
      data-ai-replace-confirm
      class="mx-3 mb-2 flex flex-col gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 p-2.5 text-xs text-n-amber-11"
    >
      <span>{{ t('EMAIL_TEMPLATES.BUILDER.AI.REPLACE_ASK') }}</span>
      <div class="flex justify-end gap-2">
        <button
          type="button"
          data-ai-replace-cancel
          class="rounded-md px-2 py-1 text-n-slate-11 hover:bg-n-solid-3"
          @click="pending = ''"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.AI.REPLACE_CANCEL') }}
        </button>
        <button
          type="button"
          data-ai-replace-yes
          class="rounded-md bg-n-brand px-2 py-1 font-medium text-white"
          @click="run(pending, { confirmed: true })"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.AI.REPLACE_YES') }}
        </button>
      </div>
    </div>
    <div class="flex flex-wrap gap-1.5 px-3 pb-2">
      <button
        v-for="chip in chips"
        :key="chip"
        type="button"
        :disabled="busy"
        class="rounded-md bg-n-solid-2 px-2 py-1 text-[11px] text-n-slate-11 hover:bg-n-solid-3 disabled:opacity-50"
        @click="onChip(chip)"
      >
        {{ t(`EMAIL_TEMPLATES.BUILDER.AI.CHIPS.${chip}`) }}
      </button>
    </div>
    <form
      class="flex items-center gap-2 border-t border-n-weak p-3"
      @submit.prevent="run(input)"
    >
      <input
        v-model="input"
        :placeholder="t('EMAIL_TEMPLATES.BUILDER.AI.PLACEHOLDER')"
        class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
      />
      <button
        type="submit"
        :disabled="busy || !input.trim()"
        class="i-lucide-send text-n-brand disabled:opacity-40"
      />
    </form>
    <p v-if="!hasContent" class="px-3 pb-2 text-[10px] text-n-slate-10">
      {{ t('EMAIL_TEMPLATES.BUILDER.AI.COST') }}
    </p>
  </div>
</template>
